import { login } from "@lib/data/customer"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import GoogleAuthButton from "@modules/account/components/google-auth-button"
import PaypalAuthButton from "@modules/account/components/paypal-auth-button"
import { useActionState } from "react"
import { useSearchParams } from "next/navigation"
import { Mail, Lock } from "lucide-react"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Login = ({ setCurrentView }: Props) => {
  const [formState, formAction] = useActionState(login, null)
  const searchParams = useSearchParams()
  const googleError =
    searchParams.get("error") === "google"
      ? "Google sign-in failed. Please try again."
      : null

  return (
    <div data-testid="login-page">
      <h2 className="text-[22px] font-extrabold text-center text-gray-900 mt-7 mb-1.5 uppercase tracking-[1.1px]">
        Sign In With Email
      </h2>
      <p className="text-center text-[15px] text-gray-500 mb-5">
        To access your account
      </p>

      <ErrorMessage
        error={formState?.error ?? googleError}
        data-testid="login-error-message"
        className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
      />

      <form className="w-full" action={formAction}>
        <div className="space-y-[15px]">
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              name="email"
              type="email"
              placeholder="Email"
              required
              autoComplete="email"
              className="w-full h-12 pl-11 pr-4 border border-brand-navy rounded-lg text-[15px] text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy outline-none"
              data-testid="email-input"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              name="password"
              type="password"
              placeholder="Password"
              required
              autoComplete="current-password"
              className="w-full h-12 pl-11 pr-4 border border-brand-navy rounded-lg text-[15px] text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy outline-none"
              data-testid="password-input"
            />
          </div>
        </div>

        <SubmitButton
          data-testid="sign-in-button"
          className="w-full h-[52px] mt-6 bg-brand-dark-navy !text-[17px] !rounded-full hover:bg-[#0c2354] transition-colors"
        >
          Login
        </SubmitButton>
      </form>

      <div className="mt-[22px] space-y-3">
        <GoogleAuthButton label="Sign in with Google" />
        <PaypalAuthButton />
      </div>

      <div className="text-center mt-[13px]">
        <button
          type="button"
          className="text-sm font-medium text-[#c41e3a] hover:underline"
        >
          Forgotten Password?
        </button>
      </div>
    </div>
  )
}

export default Login
